import { List, fromJS } from "immutable";
import { Observable } from "rxjs";
import * as Sentry from "@sentry/browser";
import dbService from "lib/dexie-db-service";
import { COLLECTIONS, MUTATE_OPERATION_ENHANCED, MUTATE_OPERATIONS_SENT, MUTATE_CLEAR } from "constants/action-types";
import {
    collectionSetPendingMutation,
    collectionRequestSavePendingMutation,
    ICollectionRequestSavePendingMutationAction,
    ICollectionRequestDeletePendingMutationAction,
} from "actions/collections";
import { ROOT_NAMESPACE, ITEMS_ID, ITEMS_NAMESPACE } from "constants/product-collection";
import * as SentryUtil from "lib/sentry-util";
import { AssortmentTransactions, AssortmentEvents } from "constants/sentry-metrics";

export const collectionSaveChangesOnMutate = (action$, store) => {
    return action$
        .ofType(MUTATE_OPERATION_ENHANCED)
        .filter(
            ({
                payload: {
                    HEAD: { namespace },
                    objectId,
                },
            }) => {
                const { mutate } = store.getState();
                const { sentMutations } = mutate;

                const sentMutation = sentMutations.get(objectId);
                // Short-circuit if sentMutation has value. We'll save the operations at MUTATE_OPERATIONS_SENT
                return !!sentMutation && namespace === ROOT_NAMESPACE && objectId === ITEMS_ID;
            },
        )
        .concatMap(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { committedHEADs, sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);

            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            const committedHEAD = committedHEADs.get(objectId);
            const collectionId = committedHEAD.document_id;
            const currentRevision = parseInt(committedHEAD.revision, 10);

            // Combine sentMutations with uncommittedMutations.
            // Don't get misled by sentMutations. There is attempted request to send these mutations but it's not necessarily successful.
            const pendingOperations = [];
            if (sentMutation) {
                sentMutation.mutations.forEach((mutation) => {
                    mutation.operations.forEach((operation) => {
                        pendingOperations.push({
                            ...operation,
                            requestId: sentMutation.requestId,
                        });
                    });
                });
            }

            uncommittedMutation.forEach((mutation) => {
                mutation.operations.forEach((operation) => {
                    pendingOperations.push(operation);
                });
            });

            // Queue operations offline for restoring purpose in case they never get synced
            return [
                collectionRequestSavePendingMutation({
                    documentId: collectionId,
                    objectNamespace: ITEMS_NAMESPACE,
                    objectId,
                    revisionId: currentRevision,
                    mutations: pendingOperations,
                }),
            ];
        });
};

export const collectionSaveChangesOnMutateSent = (action$, store) => {
    return action$
        .ofType(MUTATE_OPERATIONS_SENT)
        .filter(({ payload: { objectId, objectType } }) => objectId === ITEMS_ID && objectType === ITEMS_NAMESPACE)
        .concatMap(({ payload: { objectId } }) => {
            const { mutate } = store.getState();
            const { committedHEADs, sentMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);

            const committedHEAD = committedHEADs.get(objectId);
            const collectionId = committedHEAD.document_id;
            const currentRevision = parseInt(committedHEAD.revision, 10);

            // Combine sentMutations with uncommittedMutations.
            // Don't get misled by sentMutations. There is attempted request to send these mutations but it's not necessarily successful.
            const pendingOperations = [];
            if (sentMutation) {
                sentMutation.mutations.forEach((mutation) => {
                    mutation.operations.forEach((operation) => {
                        pendingOperations.push({
                            ...operation,
                            requestId: sentMutation.requestId,
                        });
                    });
                });
            }

            // Queue operations offline for restoring purpose in case they never get synced
            return [
                collectionRequestSavePendingMutation({
                    documentId: collectionId,
                    objectNamespace: ITEMS_NAMESPACE,
                    objectId,
                    revisionId: currentRevision,
                    mutations: pendingOperations,
                }),
            ];
        });
};

export const collectionRequestSavePendingMutationEpic = (action$) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.PENDING_MUTATION.REQUEST_SAVE)
        .concatMap(({ payload }: ICollectionRequestSavePendingMutationAction) => {
            SentryUtil.startSpan(AssortmentTransactions.Mutate, AssortmentEvents.MutatePendingMutations);
            const { documentId, objectNamespace, objectId, revisionId, mutations } = payload;

            return Observable.fromPromise(
                dbService.services.savePendingMutation({
                    objectNamespace,
                    documentId,
                    objectId,
                    revisionId,
                    mutations: fromJS(mutations).toJS(),
                }),
            ).concatMap((savedPendingMutation) => {
                SentryUtil.finishSpan(AssortmentTransactions.Mutate, AssortmentEvents.MutatePendingMutations);
                return Observable.of({
                    objectNamespace,
                    objectId,
                    pendingMutation: savedPendingMutation,
                });
            });
        })
        .concatMap(({ objectId, pendingMutation }) => {
            return [collectionSetPendingMutation(objectId, pendingMutation)];
        })
        .takeUntil(action$.ofType(MUTATE_CLEAR))
        .catch((err) => {
            Sentry.captureException(err);
            // eslint-disable-next-line no-console
            console.error(err);
            return [];
        });
};

export const collectionRequestDeletePendingMutationEpic = (action$) => {
    return action$
        .ofType(COLLECTIONS.COLLECTION.PENDING_MUTATION.REQUEST_DELETE)
        .flatMap(({ payload }: ICollectionRequestDeletePendingMutationAction) => {
            const { objectNamespace, objectId, documentId } = payload;

            dbService.services
                .deletePendingMutation({
                    documentId,
                    objectNamespace,
                    objectId,
                })
                .catch((err) => {
                    Sentry.captureException(err);
                });

            return [];
        });
};
